/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import React, { Component } from 'react';

import _ from 'underscore';

import unexpectedError from '@ba/ba-graphics/dist/illustrations-js/unknown-warning_128';
import InfoDialog from './InfoDialog';
import VisualizationPreviewList from 'lib/@waca/vis-recommender/src/js/preview/VisualizationPreviewList';
import FakeVisualizationPreviewList from  'lib/@waca/vis-recommender/src/js/preview/FakeVisualizationPreviewList';

import { observer } from 'mobx-react';
import {  PENDING, FULFILLED, REJECTED } from 'mobx-utils';
import StringResources from 'nls/StringResources';
import PropTypes from 'util/PropTypes';
import {
	openDatasetPane,
	openCardsPane
} from 'util/SidePanelUtil';

import EditDiagramDropDown from './EditDiagramDropDown';
import FieldInput, { FIELD_INPUT_DROPDOWN_MODES } from './FieldInput';
import FieldsList from '../fieldsList/FieldsList';
import FakeFieldsList from '../fieldsList/FakeFieldsList';
import FieldsListLayout from './FieldsListLayout';
import FakeFields from './FakeFields';
import Marketing from './Marketing';
import { StartingPointsViewModel } from './StartingPointsViewModel';

import VisTypeChooser from 'components/visTypeChooser';
import Empty from 'components/empty/Empty';
import Loading from 'components/empty/Loading';
import CSSHidingTransition from 'components/transition/CSSHidingTransition';

import CreateExplorationLayout from './create/CreateExplorationLayout';
import FakeCreateExplorationLayout from './create/FakeCreateExplorationLayout';
import RelationshipViewWrapper from './relationshipGraph/RelationshipViewWrapper';
import RelationshipVisErrorView from './relationshipGraph/RelationshipVisErrorView';

import RECOMMENDATION_PROPERTIES from 'constants/RECOMMENDATION_PROPERTIES';

const ERROR_DEFAULT_TITLE = StringResources.get('error_something_unexpected');
const ERROR_DEFAULT_DESCRIPTION = StringResources.get('startingPoints_error_description_relationshipDiagram');

import Config from 'components/startingPoints/relationshipGraph/Config.js';
import { parseFieldIDForExpression } from './relationshipGraph/ScopeModel';

import { Button, SVGIcon } from '@ba/ba-ui-carbon-toolkit';

const QUERY_ITEM = 'QueryItem';
const CALCULATION = 'Calculation';

import './startingPoints.scss';

const wizardClassName = 'wizard';

/*
 * Extract unique field names from interesting fields.
 * @param {object[]} interestingFields
 * @return {string[]} unique interesting field names
 */
export function _getInterestingFieldNames(interestingFields) {
	return _.chain(interestingFields)
		.map(({ name }) => name)
		.uniq()
		.value();
}

@observer
class StartingPointsLayout extends Component {

	constructor(props) {
		super(props);
		this.services = props.services;
		this.logger = this.services.biGlass.glassContext.getCoreSvc('.Logger');
		this.isNewStartingPoints = this.props.model.isNewStartingPointsFeatureEnabled();
		this.shouldUseDisambiguatedPaths = this.props.model.shouldUseDisambiguatedPaths();
		this.wizardContainerRef = React.createRef();
		this.wizardInputSectionRef = React.createRef();
		this.relationshipSectionRef = React.createRef();
		this.fieldsRef = React.createRef();
		this.state = {
			showInfoDialog: false,
			infoDialogContent: {}
		};
	}

	/**
	 * @deprecated use {@link #renderFieldsSection}
	 */
	renderOldFieldsSection() {
		const fields = this.props.model.fields.getFields();
		const allFields = fields.getAll();
		const interestingFields = fields.getInteresting();
		const toopltipID = StringResources.get('tooltipId');
		const fieldsValues = allFields.map(field => ({
			'value': field.id,
			'label': field.name,
			'title': `${field.name} (${toopltipID}: ${field.id})`
		}));
		return (
			<div className="starting-points-fields">
				<div className="starting-points-old-header">
					<h1>{StringResources.get('startingPoints_title')}</h1>
					<p>{StringResources.get('startingPoints_subtitle')}</p>
				</div>
				<div className='old-focusField'>
					<div className='focusFieldTitle'>
						{StringResources.get('startingPoints_focusField')}
					</div>
					<div className='reset-button-container'>
						<button className='reset-button' onClick={this.reset.bind(this)}>
							<span className='reset-button-mounse-focus-indicator' tabIndex='-1'>
								{/*This span is focused on when onClick instead of reset-button*/}
								{StringResources.get('reset')}
							</span>
						</button>
					</div>
				</div>
				<div className="old-fields-control">
					<FieldInput
						relationshipVM={this.props.model.relationship}
						values={fieldsValues}
						fields={this.props.model.fields}
						recommendedFields={interestingFields}
						module={this.props.activeDataSourceModule.value}
						onChange={(...params) => this.onFieldInputChange_old(...params)}
						getDropdownMaxHeight={() => this._getDropdownMaxHeight_mainPage()}
					/>
					<FieldsListLayout
						fields={interestingFields}
						focus={this.props.model.fields.getFocusField()}
						onClick={(field) => { this._handleSetFocusField(field); } }
						onFieldsChanged={(newFields) => { this.props.model.fields.setInterestingFields(newFields); }}
					/>
				</div>
			</div>
		);
	}

	renderFieldsSection() {
		const { interesting } = this.props.model.fields.getFields();
		const interestingFields = interesting.state === FULFILLED ? interesting.value : [];
		const focusField = this.props.model.fields.getFocusField();
		const dataSourceName = this.props.model.getActiveDataSourceName();

		const fields = this.props.activeDataSourceModule.value ? (
			<div className="fields-control">
				<FieldInput
					relationshipVM={this.props.model.relationship}
					glassContext = {this.services.biGlass.glassContext}
					fields={this.props.model.fields}
					module={this.props.activeDataSourceModule.value}
					recentFields={this.props.model.fields.getFields().getRecentFields()}
					recommendedFields={interestingFields}
					focusField={(focusField && focusField.name) || ''}
					placeholder={StringResources.get('startingPoints_fieldInputPlaceHolder')}
					onChange={(...params) => this.onFieldInputChange(...params)}
					getDropdownMode={({ userTextInput }) => {
						if(userTextInput && userTextInput.length) {
							return FIELD_INPUT_DROPDOWN_MODES.METADATA_TREE;
						} else {
							return FIELD_INPUT_DROPDOWN_MODES.INTERESTING_FIELDS;
						}
					}}
					getDropdownMaxHeight={() => this._getDropdownMaxHeight_mainPage()}
					activeDataSourceModule={this.props.activeDataSourceModule}
					shouldUseDisambiguatedPaths={this.shouldUseDisambiguatedPaths}
				/>
				<EditDiagramDropDown
					relationshipVM={this.props.model.relationship}
					onChangeScopeClick={this.onEditScopeClick.bind(this)}
				/>
			</div>
		) : null;

		return (
			<div className="starting-points-fields" ref={this.fieldsRef}>
				<div className="starting-points-header">
					<h1>{StringResources.get('startingPoints_newtitle')}</h1>
				</div>
				<div className='focusField'>
					<div className='datasourceTitle'>
						{dataSourceName}
					</div>
					<div className='reset-button-container'>
						<button className='reset-button' onClick={this.reset.bind(this)}>
							<span className='reset-button-mounse-focus-indicator' tabIndex='-1'>
								{/*This span is focused on when onClick instead of reset-button*/}
								{StringResources.get('reset')}
							</span>
						</button>
					</div>
				</div>
				{fields}
			</div>
		);
	}

	renderFakeFields() {
		return <FakeFields />;
	}

	renderResetButton(){
		return(
			<div className='reset-button-container'>
				<button className='reset-button' onClick={this.reset.bind(this)}>
					<span className='reset-button-mounse-focus-indicator' tabIndex='-1'>
						{/*This span is focused on when onClick instead of reset-button*/}
						{StringResources.get('reset')}
					</span>
				</button>
			</div>
		);
	}

	renderRelationshipGraph() {
		return <RelationshipViewWrapper module={this.props.activeDataSourceModule.value} startingPointsVM={this.props.model} services={this.services} fields={this.props.model.fields} shouldUseDisambiguatedPaths={this.shouldUseDisambiguatedPaths}/>;
	}
	/**
	 * Render the relationship diagram loading icon, enable the cancel button
	 * Show alternate label after 3 seconds
	 */
	renderRelationshipGraphLoader(message) {
		return <Loading
			enableTimer = {this.props.model.relationship.allowLongRunningRequest()}
			enableCancel = {Config.enable_cancel}
			cancelHandler={this._cancelRequest.bind(this)}
			description={message}
			suggestions={StringResources.get('startingPoints_relationship_background_running_suggestions')}
			timerInterval = {Config.request_progressive_status_timer_interval}
			alternateDescription = {StringResources.get('startingPoints_relationship_timed_description')}
		/>;
	}

	render() {
		if (!this.props.activeDataSourceId) {
			return (
				<div className="starting-points-static" tabIndex={-1}>
					<div className="starting-points-static__content">
						<Marketing />
					</div>
				</div>
			);
		} else if(this.props.activeDataSourceModule.state === REJECTED) {
			return (
				<Empty
					tabIndex={-1}
					icon={<SVGIcon iconId={unexpectedError.id} size="xxlarge"/>}
					title={StringResources.get('error_unexpected')}
					description={StringResources.get('error_cannot_load_exploration')}
				/>
			);
		} else if(this.props.activeDataSourceModule.state === FULFILLED){
			const isSuccess = true;
			if (this.isNewStartingPoints) {
				return (
					<div className="starting-points-page" tabIndex={-1}>
						<div className="starting-points-page-content">
							{
								this.props.model.fields.getFocusField() ?
									this.renderStartingPoints(isSuccess) :
									this.renderWizard(isSuccess)
							}
							{this.renderStartingPointsInfoDialog()}
						</div>
					</div>
				);
			}
			return (
				<div className="starting-points-page" tabIndex={-1}>
					<CSSHidingTransition
						in={!this.props.model.showModalVisTypePicker}
						classNames="explore__animation--fade">
						<div className="old-starting-points-page-content">
							{this.renderStartingPoints(isSuccess)}
						</div>
					</CSSHidingTransition>
					<VisTypeChooser
						active={this.props.model.showModalVisTypePicker}
						className="explore__animation--slideDown"
						services={this.props.services}
						dashboardApi={this.props.dashboardApi}
						onVisTypeSelected={(id) => this.onVisTypeSelected(id)}
						onClose={() => this.closeVisTypeChooser()}
					/>
				</div>
			);
		}else{
			return (<Loading description={StringResources.get('startingPoints_wizard_data_loadingTitle')}/>);
		}
	}

	renderStartingPointsInfoDialog() {
		if (this.state.showInfoDialog) {
			const { title, body } = this.state.infoDialogContent || {};
			const closeDialog = () => this.setState({
				showInfoDialog: false,
				infoDialogContent: {}
			});
			return (
				<InfoDialog
					title={title}
					body={body}
					onClose={ closeDialog }
					onClickOk={ closeDialog }
				/>
			);
		} else {
			return null;
		}
	}

	renderWizard(isSuccess) {
		if(isSuccess) {
			const fields = this.props.model.fields.getFields();
			const { state } = fields.interesting;

			if ( state === FULFILLED){
				return (
					<div className={wizardClassName} ref={this.wizardContainerRef}>
						<div className={wizardClassName + '--header'}>
							<div className={wizardClassName + '--header--text'}>
								<h1 className={wizardClassName + '--header--text--title'}>{StringResources.get('startingPoints_wizard_title')}</h1>
								<div className={wizardClassName + '--header--text--subtitle'}>{StringResources.get('startingPoints_wizard_subtitle')}</div>
							</div>
							{this.renderWizardSkip()}
						</div>
						<div className={wizardClassName + '--body'}>
							{this.renderWizardTextInput()}
							{this.renderWizardRecommendations()}
						</div>
					</div>
				);
			} else if(state === PENDING){
				return (<Loading description={StringResources.get('startingPoints_wizard_recommended_loadingTitle')}/>);
			}else if(state === REJECTED){
				return (this.renderErrorOnGettingFields(fields.interesting.value));
			} else {
				return (
					<Loading description={StringResources.get('startingPoints_wizard_loadingDataSource')}/>
				);
			}
		}
	}

	renderWizardTextInput() {
		const fields = this.props.model.fields.getFields();
		const { state, value: interestingFields } = fields.interesting;
		let instructionText = StringResources.get('startingPoints_wizard_columnField_placeholder_noFields');

		if(state === FULFILLED && interestingFields) {
			const interestingFieldNames = _getInterestingFieldNames(interestingFields);
			if(interestingFieldNames.length === 1) {
				instructionText = StringResources.get(
					'startingPoints_wizard_columnField_placeholder_suggestedField',
					{
						suggestedField: interestingFieldNames[0]
					}
				);
			} else if(interestingFieldNames.length > 1) {
				instructionText = StringResources.get(
					'startingPoints_wizard_columnField_placeholder_suggestedFields',
					{
						suggestedField1: interestingFieldNames[0],
						suggestedField2: interestingFieldNames[1]
					}
				);
			}
		}

		return (
			<div className={wizardClassName + '--body--text-input'} ref={this.wizardInputSectionRef}>
				<div className={wizardClassName + '--body--text-input--title'}>
					{StringResources.get('startingPoints_wizard_columnField_title')}
				</div>
				<FieldInput
					relationshipVM={this.props.model.relationship}
					fields={this.props.model.fields}
					module={this.props.activeDataSourceModule.value}
					placeholder={instructionText}
					onChange={(...params) => this.onFieldInputChange(...params)}
					getDropdownMode={() => FIELD_INPUT_DROPDOWN_MODES.METADATA_TREE}
					getDropdownMaxHeight={() => this._getDropdownMaxHeight_wizard()}
					activeDataSourceModule={this.props.activeDataSourceModule}
					shouldUseDisambiguatedPaths={this.shouldUseDisambiguatedPaths}
				/>
			</div>
		);
	}

	renderWizardRecommendations() {
		const fields = this.props.model.fields.getFields();
		const { state, value: interestingFields } = fields.interesting;
		if(state === PENDING) {
			return (
				<div className="wizard-section">
					<div className="wizard-section-title">{StringResources.get('startingPoints_wizard_recommended_loadingTitle')}</div>
					<FakeFieldsList numberOfFields={5} />
				</div>
			);
		} else if(state === FULFILLED) {
			if(!interestingFields || interestingFields.length === 0) {
				const title = StringResources.get('startingPoints_recommended_fields_unavailable');
				const desc = StringResources.get('startingPoints_wizard_recommendations_enter_column');
				const options = {
					error: {},
					preferredTitle: title,
					preferredDescription: desc,
					textAlign: 'left',
					iconId: null,
					suggestions: null
				};
				return (
					<div className="wizard-section">
						{this._renderErrorView(options)}
					</div>
				);
			} else {
				return (
					<div className={wizardClassName + '--body--recommended'}>
						<div className={wizardClassName + '--body--recommended--title'}>
							{StringResources.get('startingPoints_wizard_recommended_title')}
						</div>
						<FieldsList
							className={wizardClassName + '--body--recommended--list'}
							fields={interestingFields && interestingFields.length ? interestingFields.map(
								({ id, name }) => ({ id, label: name })
							) : []}
							onSelectField={
								({ id }) => this.onSetFocusField(id)
							}
							activeDataSourceModule={this.props.activeDataSourceModule}
							shouldUseDisambiguatedPaths={this.shouldUseDisambiguatedPaths}
							relationshipVM={this.props.model.relationship}
						/>
					</div>
				);
			}
		}
	}

	/**
	 * Render the error message when siganificant error happens on wizard
	 * @param  {Object} Error object
	 * @return {Object} return the relationship vis error view
	 */
	renderErrorOnGettingFields(error){
		//TODO: Cleaning up, moving the following out
		const title = StringResources.get('startingPoints_recommended_fields_unavailable');
		const msgDescription_unenriched = StringResources.get('startingPoints_unable_to_fetch_recommendations_unenriched_datasource');
		const msgDescription_unsupported = StringResources.get('startingPoints_unable_to_fetch_recommendations_unsupported_datasource');
		const sevreErrors = {
			'Smarts-IFR-49' : {
				severity: 1,
				msgTitle: title,
				msgDesc: msgDescription_unsupported
			},
			'Smarts-IFR-21' : {
				msgTitle: title,
				severity: 1,
				msgDesc: msgDescription_unenriched
			}
		};
		if(error) {
			const smartsErrorCode = error.smartsCode;
			const smartsError = sevreErrors[smartsErrorCode];
			if (!smartsError) {
				return (this._renderErrorView({ error: error }));
			}
			if (smartsError.severity === 1) {
				const options = {
					error: smartsError,
					preferredTitle: smartsError.msgTitle,
					preferredDescription: smartsError.msgDesc,
					textAlign: 'center'
				};
				return (this._renderErrorView(options));
			} else if (smartsError.severity === 2) {
				const title = StringResources.get('startingPoints_recommended_fields_unavailable');
				const desc = StringResources.get('startingPoints_wizard_recommendations_enter_column');
				const options = {
					error: smartsError,
					preferredTitle: title,
					preferredDescription: desc,
					textAlign: 'left',
					iconId: null,
					suggestions: null
				};
				return (
					<div className="wizard" ref={this.wizardContainerRef}>
						<h1>{StringResources.get('startingPoints_wizard_title')}</h1>
						<div className="wizard-instructions">{StringResources.get('startingPoints_wizard_subtitle')}</div>
						<div className="wizardSections">
							{this.renderWizardTextInput()}
							{this._renderErrorView(options)}
						</div>
					</div>
				);
			}
		} else {
			return (this._renderErrorView({ error: error }));
		}
	}

	renderWizardSkip() {
		const fields = this.props.model.fields.getFields();
		const { value: interestingFields } = fields.interesting;

		// Do not render wizard skip, if there are no interesting field
		if (!interestingFields || interestingFields.length === 0) {
			return null;
		}

		return (
			<Button
				className={wizardClassName + '--header--skip'}
				tabIndex="0"
				label={StringResources.get('startingPoints_wizard_skip_link')}
				variant="frame"
				intent="primary"
				onClick={this._skipLinkOnClick}
				onKeyDown={this._skipLinkOnKeyDown}
			/>
		);
	}


	_renderErrorView({ error, preferredTitle, preferredDescription, textAlign, iconId, suggestions }) {
		return (
			<RelationshipVisErrorView
				alignment= {textAlign}
				tabIndex={-1}
				iconId={iconId}
				error={error}
				preferredTitle={ preferredTitle }
				preferredDescription={ preferredDescription }
				suggestions = {suggestions}
			/>
		);
	}

	_skipLinkOnClick = () => {
		this._setAnyFocusField();
	}

	_skipLinkOnKeyDown = (event) => {
		switch (event.key) {
			case ' ': // space
			case 'Enter':
				this._setAnyFocusField();
				break;
		}
	}

	_setAnyFocusField() {
		const { interesting: { state, value:interestingFields } } = this.props.model.fields.getFields();
		if(state === FULFILLED && interestingFields.length) {
			//Usual case: select first interesting field
			this.onSetFocusField(interestingFields[0].id);
		} else {
			//If there's a delay loading interesting fields, select first column
			const allColumns = this.props.activeDataSourceModule.value.getMetadataColumns();
			this.onSetFocusField(allColumns[0].getId());
		}
	}

	renderStartingPoints(isSuccess) {
		if (this.isNewStartingPoints) {
			return ([
				this.renderRelationshipSection(isSuccess),
				this.renderVisualizationsSection(isSuccess)
			]);
		}
		return ([
			this.renderVisualizationsSectionOld(isSuccess),
			this.renderRelationshipSectionOld(isSuccess)
		]);
	}

	renderRelationshipSectionOld(isSuccess) {
		const relationshipSupportedState = this.props.model.relationshipSupported && this.props.model.relationshipSupported.state;
		const contents = [];

		if(relationshipSupportedState === REJECTED) {
			contents.push(
				<RelationshipVisErrorView
					error={this.props.model.relationshipSupported.error}
					defaultTitle={ERROR_DEFAULT_TITLE}
					defaultDescription={ERROR_DEFAULT_DESCRIPTION}
				/>
			);
		} else if(!isSuccess /* i.e., loading data source */) {
			contents.push(this.renderFakeFields());
			contents.push(this.renderRelationshipGraphLoader(StringResources.get('startingPoints_loadingPage')));
		} else if(!relationshipSupportedState || relationshipSupportedState === PENDING /* i.e., loading relationships */) {
			contents.push(this.renderFakeFields());
			contents.push(this.renderRelationshipGraphLoader(StringResources.get('startingPoints_loadingRelationships')));
		} else /* i.e., relationships loaded */ {
			contents.push(this.renderOldFieldsSection());
			contents.push(this.renderRelationshipGraph());
		}

		return (
			<div className="starting-points-page__oldRelationshipSection">
				{contents}
			</div>
		);
	}

	renderRelationshipSection() {
		const targetModel  = this.props.model.relationship.getTargetModel();
		const relationshipSupportedState = targetModel && targetModel.data && targetModel.data.state;
		return (
			<div className="starting-points-page__relationshipSection" ref={this.relationshipSectionRef}>
				{this.renderFieldsSection()}
				{(relationshipSupportedState === PENDING) ? this._renderPendingUI() : this.renderRelationshipGraph()}
			</div>
		);
	}

	_renderPendingUI(){
		return(
			<div className="starting-points-page__relationshipSection__pendingUI">
				{this.renderRelationshipGraphLoader(StringResources.get('startingPoints_loadingRelationshipGaph'))}
			</div>);
	}

	_cancelRequest(){
		this.props.model.relationship.cancelRequestDelegator();
	}

	/**
	 * @deprecated use {@link #renderVisualizationsSection}
	 */
	renderVisualizationsSectionOld(isSuccess) {
		const contents = [];

		if(isSuccess) {
			contents.push(
				<CreateExplorationLayout
					// Show icon when there are no recommended vis list to render
					fill={this.renderRecommendedVisList(maxVisualizations) === null}
					services={this.props.services}
					activeDataSourceId={this.props.activeDataSourceId}
					handleShowVisTypesClick={() => this._showVisTypesDialog()}
					createVis={(widgetSpecs) => this.addVisCard(widgetSpecs)}
					dashboardApi={this.props.dashboardApi}
					key='create'
				/>
			);

			const maxVisualizations = this.props.maxDisplayedItems || 3;
			contents.push(
				this.renderRecommendedVisList(maxVisualizations)
			);
		} else {
			contents.push(<FakeCreateExplorationLayout fill={true} />);
		}

		return (
			<div className="starting-points-visualizations" key="visualizations">
				{contents}
			</div>
		);
	}

	renderVisualizationsSection(isSuccess) {
		const contents = [];
		const maxVisualizations = this.props.maxDisplayedItems || 3;

		if(isSuccess) {
			contents.push(
				this.renderRecommendedVisList(maxVisualizations)
			);
			const influencers = this.props.model.getSelectedInfluencers();
			if (influencers) {
				const influencerLabel = influencers.map(({ label }) => label).join(', ');
				return (
					<div className="starting-points-visualizations">
						<div className="vis-preview-list-header">
							<h2>{StringResources.get('startingPoints_chooseVis')}</h2>
							<h3>{StringResources.get('startingPoints_exploreVisRelated') + ` '${influencerLabel}'`}</h3>
						</div>
						<div className="vis-preview-list-wrapper" key="visualizations">
							{contents}
						</div>
					</div>
				);
			} else {
				return (
					<div className="starting-points-visualizations">
						<div className="vis-preview-list-wrapper" key="visualizations">
							{contents}
						</div>
					</div>
				);
			}
		} else {
			contents.push(<FakeCreateExplorationLayout fill={true} />);
		}
		return;
	}

	_showVisTypesDialog() {
		this.props.model.setShowModalVisTypePicker(true);
	}

	onEditScopeClick() {
		const target = this.props.model.relationship.getTargetModel();
		const scope = target && target.scope;
		if(!scope) {
			return;
		}
		const dataSourcesFeature = this.props.dashboardApi.getFeature('DataSources');
		return dataSourcesFeature.getDataSource(this.props.activeDataSourceId).getModule()
			.then(module => {
				const mapIdToMoserObject = (id) => module.getMetadataColumn(id).getMoserObject();
				let recommendedFieldIds;
				let scopedFieldIds;
				let recommendedItems;
				let initialItems;
				return scope.getDefaultRecommendations()
					.then(recommendations => {
						recommendedFieldIds = recommendations.map(item => item.fieldIDForExpression);
						recommendedItems = recommendedFieldIds.map(mapIdToMoserObject);
						const scopedFields = scope.getScopedFields();
						if(scopedFields) {
							scopedFieldIds = scopedFields.map(item => item.fieldIDForExpression);
							initialItems = scopedFieldIds.map(mapIdToMoserObject);
						} else {
							scopedFieldIds = recommendedFieldIds;
							initialItems = recommendedItems;
						}
					})
					.then(() => {
						return module
							.getShapingHelper()
							.openItemPicker({
								labels: {
									title: StringResources.get('edit_scope_title'),
									subTitle: StringResources.get('edit_scope_subtitle')
								},
								initialItems: initialItems,
								recommendedItems: recommendedItems,
								max: Config.max_scoped_columns,
								transformDroppedItems: this._transformDroppedItems.bind(this),
								canDrop: this._canDrop.bind(this)
							})
							.then(selectedItems => {
								// when click cancel or close the dialog, selectedItems will be null
								if(!selectedItems || !selectedItems.length) {
									return;
								}
								const relationshipVM = this.props.model.relationship;
								//if cannot set scope, then show dialog
								const result = relationshipVM.canFetchData(target.id, 'editScope'/* action */);
								if (!result.canFetchData) {
									const { title, body } = result.infoDialogContent;
									//show dialog
									this.setState({
										infoDialogContent: {
											title,
											body
										},
										showInfoDialog: true
									});
									return;
								}
								const mapMoserObjectToId = (item) => item.getIdForExpression();
								const selectedColumnIds = selectedItems.map(mapMoserObjectToId);
								//build scoped columns
								const scopedColumns = selectedColumnIds.map(parseFieldIDForExpression);
								target.setScope(scopedColumns);
							});
					});
			});
	}

	_transformDroppedItems(items) {
		if(items && items.length) {
			const currentTargetId = this.props.model.relationship.getCurrentTargetId();
			const allowedTypes = [ QUERY_ITEM, CALCULATION ];
			items = items.filter(item => allowedTypes.indexOf(item.getObjectType()) !== -1 && item.getIdForExpression() !== currentTargetId);
		}
		return items;
	}

	_canDrop(items) {
		if(!items || items.length === 0) {
			return false;
		} else {
			return true;
		}
	}

	onVisTypeSelected(visTypeId) {
		this.closeVisTypeChooser();
		this.addVisCardFromVisType(visTypeId);
		openDatasetPane(this.props.dashboardApi);
	}

	closeVisTypeChooser() {
		this.props.model.setShowModalVisTypePicker(false);
	}

	addVisCard(widgetSpecs, tabDisplay, transactionIdPrefix = 'startingPointsCreate') {
		this.props.addVisCard(widgetSpecs, transactionIdPrefix, tabDisplay);
	}

	addVisCardFromVisType(visTypeId) {
		const widgetSpec = {
			model: {
				type: 'live',
				visId: visTypeId,
				visTypeLocked: true,
				data: {}
			}
		};
		return this.addVisCard(widgetSpec, 'slots', 'startingPointsCreate');
	}

	_onPromptingError(recommendationItem) {
		this.props.model.addRecommendationsOfStaticType(recommendationItem.key);
	}

	renderRecommendedVisList(maxDisplayedItems) {
		const fields = this.props.model.getSelectedInfluencers();

		if(!fields) {
			return null;
		}

		const fieldIds = fields.map(({ id }) => id);

		let content = null;
		const recommendations = this.props.model.getRecommendations(fieldIds);
		if(recommendations.state === PENDING) {
			content = (
				<FakeVisualizationPreviewList numberOfItems={maxDisplayedItems} isNewLayout={this.isNewStartingPoints} showTextPending={!this.isNewStartingPoints} itemSize= {this.isNewStartingPoints ? 'medium' : 'large'} />
			);
		} else if(recommendations.state === FULFILLED && recommendations.value.length) {
			const items = recommendations.value;
			content = <VisualizationPreviewList
				maxDisplayedItems={maxDisplayedItems}
				items={items}
				dashboardApi={this.props.dashboardApi}
				onClickItem={this._onSelectVisualization.bind(this)}
				onAddItem={this._onAddVisualization.bind(this)}
				additionalProperties={RECOMMENDATION_PROPERTIES}
				logger={this.logger}
				isNewLayoutEnabled={this.isNewStartingPoints}
				onPromptingError={this._onPromptingError.bind(this)}
				itemSize= {this.isNewStartingPoints ? 'medium' : 'large'}
			/>;
		} else {
			//TODO: This should be moved to @waca/vis-recommender
			content = (
				<div className='vis-preview-list-wrapper__error'>
					<Empty
						icon={<SVGIcon iconId={unexpectedError.id} size="normal" intent="danger"/>}
						description={StringResources.get('error_cannot_load_recommended')}
					>
					</Empty>
				</div>
			);
		}
		if (this.isNewStartingPoints) {
			return content;
		}
		return (
			<div className="vis-preview-list-wrapper">
				{content}
			</div>
		);
	}

	_renderRefreshButton() {
		return (
			<button className='vis-preview-list-wrapper__error__refresh-button' onClick={this._refreshVisualizations.bind(this)}>
				<span className='refresh-button-mouse-focus-indicator' tabIndex='-1'>
					{StringResources.get('refresh_visualizations')}
				</span>
			</button>
		);
	}

	_setFieldsListLayoutRef = (node) => {
		if (node) {
			this.fieldsListLayoutRef = node;
		}
	}
	_handleSetFocusField(focusFieldId) {
		this.props.model.fields.setFocusField(focusFieldId);

	}

	onFieldInputChange_old(value) {
		return this.props.model.fields.addField && this.props.model.fields.addField(value);
	}

	onFieldInputChange(value) {
		this.onSetFocusField(value);
	}

	onSetFocusField(focusFieldId) {
		const relationshipVM = this.props.model.relationship;
		const result = relationshipVM.canFetchData(focusFieldId, 'changeTarget'/* action */);
		if(result.canFetchData === true){
			this.props.model.fields.setFocusField(focusFieldId);
		} else {
			const { title, body } = result.infoDialogContent;
			//show dialog
			this.setState({
				infoDialogContent: {
					title,
					body
				},
				showInfoDialog: true
			});
		}
	}

	reset() {
		this.props.model.reset();
	}

	_onSelectVisualization(recommendation) {
		if (recommendation && recommendation.widgets && recommendation.widgets[0]) {
			this._addRecommendation(recommendation.widgets[0], 'startingPointsSelectRecommendation');
			openCardsPane(this.props.dashboardApi);
		}
	}

	_onAddVisualization(recommendation) {
		if (recommendation && recommendation.widgets && recommendation.widgets[0]) {
			this._addRecommendation(recommendation.widgets[0], 'addRelatedRecommendation');
			openCardsPane(this.props.dashboardApi);
		}
	}

	_addRecommendation(recommendation, transactionIdPrefix) {
		if(recommendation.spec && recommendation.spec.properties){
			recommendation.spec.properties.some((prop) => {
				if (prop.id === 'widget.legend.display') {
					prop.value = true; // re-adding legend once previews generated
					return true;
				}
			});
		}
		const isRecommendationOfStaticType = this.props.model.isRecommendationOfStaticType(recommendation.key);
		const widgetSpecs = {
			model: Object.assign({
				type: 'live',
				visTypeLocked: true
			}, recommendation.spec),
			layoutProperties: {
				isStaticWidget: isRecommendationOfStaticType
			}
		};
		return this.addVisCard(widgetSpecs, undefined, transactionIdPrefix);
	}

	_refreshVisualizations() {
		this.props.model.loadVisForSelectedInfluencers();
	}

	_getDropdownMaxHeight_wizard() {
		const containerNode = this.wizardContainerRef.current;
		const inputNode = this.wizardInputSectionRef.current;
		if (containerNode && inputNode) {
			return containerNode.clientHeight - inputNode.clientHeight;
		}
		return 0;
	}

	_getDropdownMaxHeight_mainPage() {
		const relationshipNode = this.relationshipSectionRef.current;
		const fieldsNode = this.fieldsRef.current;
		if (relationshipNode && fieldsNode) {
			return relationshipNode.clientHeight - fieldsNode.clientHeight;
		}
		return 0;
	}
}

StartingPointsLayout.propTypes = {
	dashboardApi: PropTypes.object.isRequired,
	model: PropTypes.stateTree(StartingPointsViewModel).isRequired,
	maxDisplayedItems: PropTypes.object,
	services: PropTypes.object.isRequired,
	activeDataSourceId: PropTypes.string,
	activeDataSourceModule: PropTypes.observableObject,	//TODO: Merge with activeDataSourceId. Right now, this is only being used to determine whether activeDataSourceId represents an existing data source
	addVisCard: PropTypes.func.isRequired
};

export default StartingPointsLayout;



// WEBPACK FOOTER //
// ./src/js/components/startingPoints/StartingPointsLayout.js